/**
 * Track E — main-thread client for the photo worker.
 *
 * Cancellation is two-tier: POST a CancelRequest so the worker can bail
 * cooperatively between stages (and stay alive for reuse), then hard-respawn as a
 * fallback so a wedged synchronous run can never block the next job.
 */
import type { CancelRequest, PhotoResponse, PhotoResult, PhotoSettings, RawImage } from "../core/types";
import type { JobProgress } from "./pixelateClient";
import { PhotoCancelled } from "../engine/photo";

interface Pending {
  jobId: number;
  resolve: (result: PhotoResult) => void;
  reject: (error: Error) => void;
  onProgress?: (p: JobProgress) => void;
}

export class PhotoClient {
  private worker: Worker | null = null;
  private nextJobId = 1;
  private pending: Pending | null = null;

  private spawn(): Worker {
    const worker = new Worker(new URL("./photoWorker.ts", import.meta.url), { type: "module" });
    worker.onmessage = (ev: MessageEvent): void => this.handle(ev.data as PhotoResponse);
    worker.onerror = (): void => this.fail("Photo worker crashed.");
    // A structured-clone failure surfaces here instead of as a normal message;
    // reject the pending job rather than hang forever, and respawn the worker.
    worker.onmessageerror = (): void => this.fail("Photo worker message could not be read.");
    this.worker = worker;
    return worker;
  }

  /** Terminate the crashed worker (never reuse it dead) and reject the pending job. */
  private fail(message: string): void {
    const p = this.pending;
    this.respawn();
    if (!p) return;
    this.pending = null;
    p.reject(new Error(message));
  }

  private respawn(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }

  /** POST a cooperative cancel for the given job so the (live) worker can bail. */
  private postCancel(jobId: number): void {
    if (this.worker) {
      const cancel: CancelRequest = { type: "cancel", jobId };
      this.worker.postMessage(cancel);
    }
  }

  run(
    image: RawImage,
    settings: PhotoSettings,
    options: { onProgress?: (p: JobProgress) => void },
  ): Promise<PhotoResult> {
    // Single-job client: supersede any in-flight job before starting a new one.
    this.cancel();
    const jobId = this.nextJobId++;
    const worker = this.worker ?? this.spawn();
    return new Promise<PhotoResult>((resolve, reject) => {
      this.pending = { jobId, resolve, reject, onProgress: options.onProgress };
      // Clone the pixel buffer so the caller's RawImage stays intact; transfer
      // the clone to hand ownership to the worker without a structured-clone copy.
      const data = new Uint8ClampedArray(image.data);
      const payload: RawImage = {
        width: image.width,
        height: image.height,
        name: image.name,
        data,
      };
      worker.postMessage({ type: "photo", jobId, image: payload, settings }, [
        data.buffer as ArrayBuffer,
      ]);
    });
  }

  /**
   * Cancel the in-flight job. Posts a cooperative CancelRequest first (so a
   * cooperative worker can bail and be reused), then hard-respawns as a fallback
   * for a run wedged in a synchronous stage. Pending run() rejects.
   */
  cancel(): void {
    if (this.pending) {
      const p = this.pending;
      this.pending = null;
      this.postCancel(p.jobId);
      p.reject(new PhotoCancelled("Photo job cancelled."));
    }
    this.respawn();
  }

  /** Tear down the client and its worker (e.g. from a pagehide handler). */
  dispose(): void {
    if (this.pending) {
      const p = this.pending;
      this.pending = null;
      p.reject(new PhotoCancelled("Photo client disposed."));
    }
    this.respawn();
  }

  private handle(msg: PhotoResponse): void {
    const p = this.pending;
    if (!p || msg.jobId !== p.jobId) return;
    if (msg.type === "progress") {
      p.onProgress?.({ stage: msg.stage, fraction: msg.fraction, pixelCount: msg.pixelCount });
    } else if (msg.type === "done") {
      this.pending = null;
      p.resolve(msg.result);
    } else if (msg.type === "error") {
      this.pending = null;
      p.reject(new Error(msg.message));
    } else if (msg.type === "cancelled") {
      this.pending = null;
      p.reject(new PhotoCancelled("Photo job cancelled."));
    }
  }
}
